"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ShopItem } from "../../shop.types";
import apointsImg from "../../../vip/assets/images/apoints.png";
import BackgroundImage from "../../../../../../components/ui/BackgroundImage";
import { panelVariants } from "./animations";

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface ShopOfferModalProps {
  item: ShopItem | null;
  isOpen: boolean;
  onClose: () => void;
  onPurchase: (item: ShopItem) => void;
}

// ─── Countdown ─────────────────────────────────────────────────────────────────

interface TimeLeft {
  h: number;
  m: number;
  s: number;
}

function useCountdown(isActive: boolean): TimeLeft {
  const [t, setT] = useState<TimeLeft>({ h: 10, m: 47, s: 23 });
  useEffect(() => {
    if (!isActive) return;
    const id = setInterval(() => {
      setT((prev) => {
        const { h, m, s } = prev;
        if (s > 0) return { h, m, s: s - 1 };
        if (m > 0) return { h, m: m - 1, s: 59 };
        if (h > 0) return { h: h - 1, m: 59, s: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isActive]);
  return t;
}

const pad = (n: number) => String(n).padStart(2, "0");

// ─── Portal ─────────────────────────────────────────────────────────────────────

const ModalPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const elRef = useRef<HTMLDivElement | null>(null);
  if (!elRef.current) elRef.current = document.createElement("div");
  useEffect(() => {
    const el = elRef.current!;
    document.body.appendChild(el);
    return () => {
      document.body.removeChild(el);
    };
  }, []);
  return createPortal(children, elRef.current);
};

// ─── Component ─────────────────────────────────────────────────────────────────

export default function ShopOfferModal({
  item,
  isOpen,
  onClose,
  onPurchase,
}: ShopOfferModalProps) {
  const prefersReduced = useReducedMotion();
  const timeLeft = useCountdown(isOpen);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handlePurchase = useCallback(() => {
    if (item) onPurchase(item);
    onClose();
  }, [item, onPurchase, onClose]);

  const coverImage = item?.modalCoverImage ?? item?.backgroundImage;

  const content = (
    <>
      <AnimatePresence>
        {isOpen && item && (
          <motion.div
            key="shop-offer-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 flex items-center justify-center p-6"
            style={{
              background: "rgba(0,0,0,0.65)",
              zIndex: 5000000,
            }}
            onClick={onClose}
          >
            <motion.div
              key="shop-offer-panel"
              variants={prefersReduced ? undefined : panelVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-[490px] rounded-2xl overflow-hidden"
              style={{
                background: "#050601",
                willChange: "transform",
                boxShadow: [
                  "0 0 0 1px rgba(128,148,29,0.3)",
                  "0 0 80px rgba(100,120,10,0.35)",
                  "0 0 160px rgba(60,75,5,0.2)",
                  "0 32px 80px rgba(0,0,0,0.8)",
                ].join(", "),
              }}
            >
              {/* ── EXPIRING SOON top banner ── */}
              <div
                className="absolute top-0 left-0 right-0 z-30 flex items-center justify-center gap-2 py-[7px]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(80,100,5,0.9) 20%, rgba(128,148,29,0.95) 50%, rgba(80,100,5,0.9) 80%, transparent 100%)",
                }}
              >
                <span className="text-[9px]">⚡</span>
                <span className="bebas-neue-font text-white text-[0.82rem] tracking-[0.25em]">
                  OFERTA LIMITATA — EXPIRA IN CURAND
                </span>
                <span className="text-[9px]">⚡</span>
              </div>

              {/* ── Dramatic cover strip ── */}
              <BackgroundImage
                imageSrc={coverImage!}
                imageClassName="!object-cover !rounded-none"
                className="h-[165px] relative mt-[30px]"
              >
                {/* Deep bottom-burn vignette */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 35%, rgba(5,6,1,0.97) 100%)",
                  }}
                />
                {/* Side burns */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to right, rgba(5,6,1,0.65) 0%, transparent 18%, transparent 82%, rgba(5,6,1,0.65) 100%)",
                  }}
                />
                {/* Green color wash overlay */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "radial-gradient(ellipse at 50% 120%, rgba(128,148,29,0.18) 0%, transparent 70%)",
                  }}
                />

                {/* Close button */}
                <motion.button
                  onClick={onClose}
                  whileHover={{
                    scale: 1.12,
                    rotate: 90,
                    transition: { type: "spring", stiffness: 400, damping: 18 },
                  }}
                  whileTap={{ scale: 0.88 }}
                  className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(0,0,0,0.6)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M1 1l8 8M9 1L1 9"
                      stroke="rgba(255,255,255,0.6)"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </motion.button>

                {/* AP cost badge — top left */}
                {item.apoints !== undefined && (
                  <div
                    className="absolute top-3 left-3 flex items-center gap-1.5 rounded-lg px-3 py-1.5"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(128,148,29,0.25) 0%, rgba(70,85,10,0.18) 100%)",
                      backdropFilter: "blur(14px)",
                      border: "1px solid rgba(128,148,29,0.5)",
                      boxShadow:
                        "0 0 16px rgba(128,148,29,0.3), inset 0 1px 0 rgba(200,220,80,0.2)",
                    }}
                  >
                    <img
                      src={apointsImg}
                      width={15}
                      height={15}
                      draggable={false}
                    />
                    <span
                      className="gilroy-font !font-[700] text-[0.85rem] leading-none"
                      style={{ color: "#C8DC3C" }}
                    >
                      {item.apoints} AP
                    </span>
                  </div>
                )}

                {/* Title at bottom */}
                <div className="absolute bottom-0 left-0 right-0 px-5 pb-4">
                  <h2
                    className="bebas-neue-font text-white text-[2.6rem] leading-[0.92]"
                    style={{
                      textShadow:
                        "0 0 30px rgba(128,148,29,0.55), 0 0 8px rgba(160,185,40,0.35), 0 2px 16px rgba(0,0,0,0.9)",
                    }}
                  >
                    {item.name}
                  </h2>
                  {item.description && (
                    <p
                      className="chakra-petch-font text-[0.7rem] mt-1"
                      style={{ color: "rgba(180,210,60,0.65)" }}
                    >
                      {item.description}
                    </p>
                  )}
                </div>
              </BackgroundImage>

              {/* ── Body ── */}
              <div
                className="px-5 pb-5 pt-3.5 flex flex-col gap-3"
                style={{ background: "#050601" }}
              >
                {/* Countdown timer */}
                <div
                  className="flex items-center justify-between rounded-xl px-4 py-3"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(80,100,5,0.18) 0%, rgba(50,65,3,0.12) 100%)",
                    border: "1px solid rgba(128,148,29,0.3)",
                    boxShadow: "inset 0 1px 0 rgba(160,185,40,0.1)",
                  }}
                >
                  <span
                    className="chakra-petch-font text-[0.63rem] uppercase tracking-[0.16em]"
                    style={{ color: "rgba(160,185,40,0.75)" }}
                  >
                    ⚡ Expira in
                  </span>
                  <div className="flex items-center gap-1">
                    {[
                      { val: pad(timeLeft.h), label: "ORE" },
                      { val: pad(timeLeft.m), label: "MIN" },
                      { val: pad(timeLeft.s), label: "SEC" },
                    ].map((unit, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && (
                          <span
                            className="gilroy-font !font-[800] text-[1rem] pb-1"
                            style={{ color: "rgba(128,148,29,0.5)" }}
                          >
                            :
                          </span>
                        )}
                        <div className="flex flex-col items-center">
                          <span
                            className="gilroy-font !font-[800] text-[1.15rem] tabular-nums leading-none"
                            style={{
                              color: "#A0BC22",
                              textShadow: "0 0 14px rgba(128,148,29,0.75)",
                            }}
                          >
                            {unit.val}
                          </span>
                          <span
                            className="chakra-petch-font text-[0.48rem] tracking-widest mt-0.5"
                            style={{ color: "rgba(128,148,29,0.4)" }}
                          >
                            {unit.label}
                          </span>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Modal icon */}
                {(item.modalIcon ?? item.icon) && (
                  <div className="flex justify-center">
                    <img
                      src={item.modalIcon ?? item.icon}
                      draggable={false}
                      className="w-[220px] h-18 object-contain"
                    />
                  </div>
                )}

                {/* Perks */}
                {item.perks && item.perks.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <p
                      className="chakra-petch-font text-[0.6rem] uppercase tracking-[0.15em]"
                      style={{ color: "rgba(255,255,255,0.22)" }}
                    >
                      Ce primesti
                    </p>
                    <div className="flex flex-col gap-1">
                      {item.perks.map((perk, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{
                              background:
                                "linear-gradient(135deg, #A0BC22, #6B7D12)",
                              boxShadow: "0 0 6px rgba(128,148,29,0.65)",
                            }}
                          />
                          <span
                            className="gilroy-font !font-[500] text-[0.83rem]"
                            style={{ color: "rgba(255,255,255,0.68)" }}
                          >
                            {perk}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Feature badges */}
                {item.features && item.features.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {item.features.map((feature, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-1.5 rounded-md px-2.5 py-1"
                        style={{
                          background: "rgba(128,148,29,0.07)",
                          border: "1px solid rgba(128,148,29,0.18)",
                        }}
                      >
                        {feature.icon && (
                          <feature.icon
                            style={{
                              color: "rgba(160,185,40,0.7)",
                              fontSize: "0.7rem",
                            }}
                          />
                        )}
                        <span
                          className="gilroy-font !font-[500] text-[0.74rem]"
                          style={{ color: "rgba(255,255,255,0.52)" }}
                        >
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Cost row */}
                {item.apoints !== undefined && (
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{
                      border: "1px solid rgba(128,148,29,0.28)",
                      background:
                        "linear-gradient(135deg, rgba(128,148,29,0.1) 0%, rgba(60,75,5,0.07) 100%)",
                      boxShadow: "inset 0 1px 0 rgba(160,185,40,0.1)",
                    }}
                  >
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex flex-col">
                        <span
                          className="chakra-petch-font text-[0.57rem] uppercase tracking-widest"
                          style={{ color: "rgba(255,255,255,0.22)" }}
                        >
                          Cost total
                        </span>
                        <div className="flex items-center gap-2 mt-0.5">
                          <img
                            src={apointsImg}
                            width={20}
                            height={20}
                            draggable={false}
                          />
                          <span
                            className="gilroy-font !font-[800] text-[1.55rem] leading-tight"
                            style={{
                              color: "#C8DC3C",
                              textShadow: "0 0 22px rgba(160,185,40,0.65)",
                            }}
                          >
                            {item.apoints} AP
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div
                  className="h-[1px]"
                  style={{
                    background:
                      "linear-gradient(to right, transparent, rgba(128,148,29,0.18), transparent)",
                  }}
                />

                {/* BUY NOW */}
                <div className="flex flex-col gap-2">
                  <motion.button
                    onClick={handlePurchase}
                    whileHover={
                      prefersReduced
                        ? undefined
                        : {
                            scale: 1.022,
                            boxShadow: [
                              "0 0 0 1.5px rgba(160,185,40,0.7)",
                              "0 0 40px rgba(128,148,29,0.55)",
                              "0 0 80px rgba(80,100,10,0.3)",
                              "inset 0 1px 0 rgba(220,240,120,0.2)",
                            ].join(", "),
                            transition: {
                              type: "spring",
                              stiffness: 400,
                              damping: 16,
                            },
                          }
                    }
                    whileTap={
                      prefersReduced
                        ? undefined
                        : {
                            scale: 0.972,
                            transition: {
                              type: "spring",
                              stiffness: 600,
                              damping: 20,
                            },
                          }
                    }
                    className="relative overflow-hidden w-full rounded-xl py-4 bebas-neue-font text-[1.55rem] text-white"
                    style={{
                      background:
                        "linear-gradient(135deg, #2E3A05 0%, #526A0E 25%, #80941D 52%, #526A0E 78%, #2E3A05 100%)",
                      boxShadow: [
                        "0 0 0 1px rgba(128,148,29,0.45)",
                        "0 8px 28px rgba(80,100,10,0.55)",
                        "0 2px 8px rgba(0,0,0,0.6)",
                      ].join(", "),
                    }}
                  >
                    {/* Gradient border top-glow */}
                    <div
                      className="absolute left-0 top-0 w-full h-full p-[1px] pointer-events-none rounded-xl"
                      style={{
                        background:
                          "radial-gradient(65% 80% at 50% 35%, rgba(220,240,80,1) 0%, rgba(128,148,29,0.5) 50%, rgba(128,148,29,0) 100%)",
                        WebkitMask:
                          "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                        WebkitMaskComposite: "xor",
                        maskComposite: "exclude",
                      }}
                    />
                    <span style={{ position: "relative", letterSpacing: "0.1em" }}>
                      ACHIZITIONEAZA ACUM
                    </span>
                  </motion.button>

                  <motion.button
                    onClick={onClose}
                    whileHover={{ opacity: 0.7, transition: { duration: 0.15 } }}
                    whileTap={{ scale: 0.97 }}
                    className="chakra-petch-font text-[0.68rem] py-1"
                    style={{ color: "rgba(255,255,255,0.22)" }}
                  >
                    Renunta
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  if (typeof window === "undefined") return null;
  return <ModalPortal>{content}</ModalPortal>;
}
